import React from "react";

import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

/**
 * The attach picker, used from **both** sides of the entity/media relation: the entity options under
 * a file, and the file options under an entity. One component because the two are the same list with
 * the arguments swapped — and because a second one would be where the two sides start to disagree.
 *
 * Multi-select, so it stays open on click: attaching three files to a person is one gesture.
 */

export interface AttachOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
  checked: boolean;
}

interface AttachMenuProps {
  anchorEl: HTMLElement | null;
  options: AttachOption[];
  /** Shown in place of the list when there is nothing on the other side yet. */
  emptyLabel: string;
  onToggle: (id: string) => void;
  onClose: () => void;
}

export const AttachMenu: React.FC<AttachMenuProps> = ({
  anchorEl,
  options,
  emptyLabel,
  onToggle,
  onClose,
}) => (
  <Menu
    open={anchorEl !== null}
    anchorEl={anchorEl}
    onClose={onClose}
    slotProps={{ paper: { sx: { maxHeight: 260, minWidth: 250 } } }}
  >
    {options.length === 0 ? (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ px: 2, py: 1.25, maxWidth: 260, whiteSpace: "normal" }}
      >
        {emptyLabel}
      </Typography>
    ) : (
      options.map((option) => (
        // Deliberately not `dense`: App.css still carries a legacy `.MuiMenuItem-dense` rule from
        // the react-admin sidebar (white text, grey separators, its own padding, all `!important`),
        // and `StyledEngineProvider injectFirst` puts it ahead of anything `sx` can say. A dense
        // item here renders white-on-white until it is hovered.
        <MenuItem key={option.id} onClick={() => onToggle(option.id)}>
          <ListItemIcon>
            {option.checked ? (
              <CheckBox fontSize="small" color="primary" />
            ) : (
              <CheckBoxOutlineBlank fontSize="small" color="disabled" />
            )}
          </ListItemIcon>
          {option.icon && (
            // Same story as the item itself: `.MuiListItemIcon-root` is globally `color: #ccc
            // !important`, so the type icon has to be coloured on the svg rather than on its box.
            <ListItemIcon
              sx={{
                minWidth: 28,
                "& .MuiSvgIcon-root": { color: "action.active" },
              }}
            >
              {option.icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={option.label}
            slotProps={{
              primary: {
                variant: "body2",
                noWrap: true,
                sx: { maxWidth: 200 },
              },
            }}
          />
        </MenuItem>
      ))
    )}
  </Menu>
);
